import { useState, useRef, useEffect, Fragment } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import styled from 'styled-components';

import useInfiniteQueryHook from '@/hooks/useInfiniteQueryHook';
import useIntersect from '@/hooks/useIntersect';
import DividerDefault from '@/components/Divider/Default';
import QnAInfoByStatus from '@/components/ContextPrinter/QnAInfoByStatus';
import ListEmpty from '@/containers/common/error/ListEmpty';
import ErrorPartial from '@/containers/common/error/Partial';
import { QuestionList } from '@/models/ina';
import { API, URLs } from '@/api';

interface IProps {
    category: number;
    inaList?: QuestionList[];
    saveScroll: () => void;
    setIsLoadDataMore: () => void;
}

const QuestionListItem = ({ category, inaList, saveScroll, setIsLoadDataMore }: IProps) => {
    const router = useRouter();
    const [renderList, setRenderList] = useState<QuestionList[] | undefined>(inaList);
    const [isLoading, setIsLoading] = useState(true);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const [page, setPage] = useState(1);
    const isScrollingRef = useRef<boolean>(true);

    const {
        data: questionList,
        fetchNextPage,
        hasNextPage,
        isFetching,
    } = useInfiniteQueryHook(
        ['ina-question', category],
        async ({ pageParam = page }) => {
            return await API.get(URLs.ina.questionList, { category, page: pageParam });
        },
        {
            onSuccess: res => {
                if (res.pageParams.length > 1) {
                    let list: QuestionList[] = [];

                    res.pages.forEach((page: any) => {
                        list.push(...page.data);
                    });

                    setRenderList(list);
                    setTimeout(() => {
                        setIsLoadDataMore!();
                    }, 0);
                }
            },
            cacheTime: 0,
            staleTime: 0,
        },
    );

    const fetchQuestionList = async () => {
        if (hasNextPage && !isFetching) {
            setIsLoading(true);
            setPage(prev => prev + 1);
            await fetchNextPage();
        }
    };

    useEffect(() => {
        if (isScrollingRef.current) {
            const prev = window.sessionStorage.getItem('prevPage');
            const currentTab = Number(router.query?.category ?? 1);
            const pageListStr = window.sessionStorage.getItem('ina-page');
            const scrollYStr = window.localStorage.getItem(`${router.pathname}-scroll-y`);
            const IS_SCROLLING =
                // TODO: Retest this code later
                prev && prev === '/ina/[id]' && currentTab === category + 1 && pageListStr && scrollYStr;

            if (IS_SCROLLING) {
                setPaginationLoading(true);
                const scrollYList = JSON.parse(scrollYStr);
                const pageList = JSON.parse(pageListStr);

                window.scrollTo({
                    left: 0,
                    top: scrollYList[currentTab],
                    behavior: 'smooth',
                });

                if (pageList[category] > page) fetchQuestionList();
                else if (pageList[category] === page) {
                    fetchQuestionList().then(() => {
                        setTimeout(() => {
                            setIsLoadDataMore!();
                            setPaginationLoading(false);
                        }, 500);
                    });
                }
            }
        }
    }, [questionList]);

    const handleIntersect = useIntersect(async () => {
        isScrollingRef.current = false;

        const currentTab = Number(router.query?.category ?? 1);

        if (currentTab !== category + 1) return;
        if (questionList && questionList.length === 0) {
            setIsLoading(false);
            return;
        }

        if (hasNextPage && !isFetching) {
            setIsLoading(true);
            setPage(prev => prev + 1);
            await fetchNextPage();
        } else {
            setIsLoading(false);
        }
    });

    const onClickQuestion = (questionId: number) => {
        saveScroll();

        if (typeof window !== 'undefined') {
            const pageListStr = window.sessionStorage.getItem('ina-page');

            if (pageListStr) {
                const pageList = JSON.parse(pageListStr);
                pageList[category] = page;
                window.sessionStorage.setItem('ina-page', JSON.stringify(pageList));
            } else {
                const pageList = new Array(6).fill(1);
                pageList[category] = page;
                window.sessionStorage.setItem('ina-page', JSON.stringify(pageList));
            }
        }

        router.push(
            {
                pathname: `/ina/${questionId}`,
                query: {
                    isSearching: false,
                },
            },
            `/ina/${questionId}`,
        );
    };

    const renderItem = (list: QuestionList[]) => {
        return list.map((item: QuestionList, index: number) => {
            return (
                <div key={`${item.id}_${index}`} onClick={() => onClickQuestion(item.id)}>
                    <QnAInfoByStatus
                        questionId={item.id}
                        status={item.status}
                        content={item.content}
                        answerCount={item.answerCount}
                        showStatus={true}
                        contentLine={1}
                        // style={{ padding: '18px 30px' }}
                        hasDivider
                        isLast={list.length - 1 === index}
                        contentTitle={item.title}
                    />
                </div>
            );
        });
    };

    const renderQuestions = (list: QuestionList[]) => {
        if (list.length === 0) return <ListEmpty text={'아직 등록된 질문이 없어요'} />;

        if (paginationLoading) {
            const pageListStr = window.sessionStorage.getItem('ina-page');
            const pageList = JSON.parse(pageListStr!);

            return new Array(pageList[category] * 10).fill(0).map((_, index) => {
                return (
                    <Fragment key={index}>
                        <LoadingContentWrapper>
                            <LoadingContent width="18px" />
                            <div className="content">
                                <LoadingContent width="100%" />
                                <LoadingContent width="100%" />
                                <div className="sub_info">
                                    <LoadingContent width="40px" />
                                    <LoadingContent width="40px" />
                                </div>
                            </div>
                        </LoadingContentWrapper>
                        <DividerDefault thickness={1} length="100%" color="#f1f1f1" />
                    </Fragment>
                );
            });
        }

        return (
            <>
                {renderItem(list)}
                {isLoading && (
                    <LoadingWrapper>
                        <Image src={`/img/common/loading_grey.gif`} width={50} height={50} alt="loading" />
                    </LoadingWrapper>
                )}
                <TargetOfInfiniteScroll ref={handleIntersect} />
            </>
        );
    };

    return (
        <Container>
            {renderList ? (
                <>{renderQuestions(renderList)}</>
            ) : (
                <ErrorContainer>
                    <ErrorPartial type="communication" />
                </ErrorContainer>
            )}
        </Container>
    );
};

const Container = styled.div``;

const LoadingContentWrapper = styled.div`
    ${({ theme }) => theme.flex.flexOnly};
    width: 100%;
    padding: 18px 30px;
    gap: 14px;

    .content {
        ${({ theme }) => theme.flex.flexColumn};
        width: 100%;
        gap: 3px;
    }

    .sub_info {
        ${({ theme }) => theme.flex.flexOnly};
        ${({ theme }) => theme.flex.justifyBetween};
        margin-top: 13px;
    }
`;

const LoadingContent = styled.div<{ width: string }>`
    width: ${({ width }) => width};
    height: 18px;
    background-color: #f1f1f1;
`;

const ErrorContainer = styled.div`
    padding-top: 50px;
    padding-bottom: 100px;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const TargetOfInfiniteScroll = styled.div`
    height: 1px;
`;

export default QuestionListItem;
